import { isMembershipBacked, systemPipeline, systemQueueAdmits } from 'app/components/action-center/systemPipelines'
import type { ReconcileAction, ReconcileContact, ReconcilePlan } from 'app/types/system-pipeline-reconcile'

const RECONCILABLE_PIPELINE_IDS: readonly string[] = [`system:hygiene`, `system:treatment`]

export function reconcileAction(
  pipelineId: string,
  contact: ReconcileContact,
  now: Date = new Date(),
): ReconcileAction {
  const isMember = (contact.pipelines ?? []).some((membership) => membership?.id === pipelineId)
  const admits = systemQueueAdmits(pipelineId, contact, now)
  if (admits && !isMember) return `add`
  if (!admits && isMember) return `stale`
  return `ok`
}

export function reconcilablePipelineIds(): string[] {
  return RECONCILABLE_PIPELINE_IDS.filter((id) => {
    const pipeline = systemPipeline(id)
    return !!pipeline && isMembershipBacked(pipeline)
  })
}

export function planForPipeline(
  pipelineId: string,
  contacts: ReconcileContact[],
  now: Date = new Date(),
): ReconcilePlan {
  const plan: ReconcilePlan = { add: [], stale: [], ok: 0 }
  for (const contact of contacts) {
    const action = reconcileAction(pipelineId, contact, now)
    if (action === `add`) plan.add.push(contact)
    else if (action === `stale`) plan.stale.push(contact)
    else plan.ok++
  }
  return plan
}

/**
 * Wider than the queue on purpose: this only narrows what gets paged through, and `reconcileAction`
 * decides. The membership clause is what makes a stale row reachable at all — without it the search
 * could only ever return contacts the queue already wants.
 */
export function candidateSearch(pipelineId: string): string {
  const member = `pipelines.id:"${pipelineId}"`
  if (pipelineId === `system:treatment`) return `(unbookedTreatmentValue:[1 TO *] OR ${member})`
  if (pipelineId === `system:hygiene`) return `(_exists_:hygieneDueDate OR ${member})`
  return member
}
